import React, { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Database, DownloadSimple, ArrowClockwise, Clock, ShieldCheck, ArrowCounterClockwise, MagnifyingGlass, CheckCircle } from "@phosphor-icons/react";
import { dmYhm } from "@/lib/dateFmt";

const fmtBytes = (n) => {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const formatWhen = (iso) => dmYhm(iso) || "—";

export default function Backups() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);

    // Restore-a-party modal state
    const [restoreOpen, setRestoreOpen] = useState(false);
    const [restoreBackup, setRestoreBackup] = useState(null); // { name, created_at }
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [restoringId, setRestoringId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/backups");
            setRows(Array.isArray(data) ? data : []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const createNow = async () => {
        setCreating(true);
        try {
            const { data } = await api.post("/admin/backups/create");
            toast.success(`Backup created: ${data.name}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCreating(false); }
    };

    const download = async (name) => {
        try {
            const token = localStorage.getItem("micm_token");
            const res = await fetch(`${API_BASE}/admin/backups/${encodeURIComponent(name)}`, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = name; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(`Download failed · ${e.message || e}`); }
    };

    const openRestore = (b) => {
        setRestoreBackup(b);
        setSearchTerm("");
        setSearchResults([]);
        setRestoreOpen(true);
    };

    const doSearch = async () => {
        if (!restoreBackup) return;
        setSearching(true);
        try {
            const { data } = await api.get(`/admin/backups/${encodeURIComponent(restoreBackup.name)}/search-parties`, {
                params: { q: searchTerm.trim() },
            });
            setSearchResults(data.parties || []);
            if ((data.parties || []).length === 0) toast.message("No parties in this backup match your search.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSearching(false); }
    };

    const restoreParty = async (p) => {
        if (!restoreBackup) return;
        const label = `${p.name}${p.phone ? " · " + p.phone : ""}`;
        const confirmMsg = p.already_exists
            ? `Party "${label}" already exists in the live database. Overwrite it with the version from this backup (portal login + transactions + slips will be MERGED, never overwritten)?`
            : `Restore party "${label}" from ${restoreBackup.name}?\n\nWill also restore up to ${p.backup_counts.transactions} transaction(s), ${p.backup_counts.uploads} slip(s) and ${p.backup_counts.users} portal login(s). Nothing already in the live DB will be overwritten.`;
        if (!window.confirm(confirmMsg)) return;
        setRestoringId(p.id);
        try {
            const { data } = await api.post(`/admin/backups/${encodeURIComponent(restoreBackup.name)}/restore-party`, {
                party_id: p.id,
            });
            const c = data.counts || {};
            toast.success(
                `Restored "${data.party_name}" · users +${c.users}, txns +${c.transactions}, slips +${c.uploads}${c.pricing_rules ? ", pricing +" + c.pricing_rules : ""}`,
                { duration: 6000 },
            );
            // Re-run search to refresh the "already exists" flags
            await doSearch();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRestoringId(null); }
    };

    const autoRows = rows.filter((r) => r.auto);
    const manualRows = rows.filter((r) => !r.auto);
    const latest = rows[0];

    return (
        <div data-testid="backups-page" className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-900">Automatic Backups</h1>
                    <p className="mt-1 text-sm text-slate-500">
                        Snapshots of parties, transactions, uploads and users. Auto-run every 24 hours; last 7 auto-snapshots retained. Manual backups are kept forever. Click <b>Restore party</b> on any row to bring back a specific accidentally-deleted party.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={load}>
                        <ArrowClockwise size={14} className="mr-1.5" /> Refresh
                    </Button>
                    <Button size="sm" onClick={createNow} disabled={creating} data-testid="backup-create-btn">
                        <ShieldCheck size={14} className="mr-1.5" /> {creating ? "Creating…" : "Create backup now"}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500">
                        <Clock size={13} /> Latest snapshot
                    </div>
                    <div className="mt-1 font-display text-lg font-bold text-slate-900">
                        {latest ? formatWhen(latest.created_at) : "—"}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">
                        {latest ? `${latest.auto ? "Automatic" : "Manual"} · ${fmtBytes(latest.size_bytes)}` : "No backups yet"}
                    </div>
                </div>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-4 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-emerald-700">Auto snapshots</div>
                    <div className="mt-1 font-display text-2xl font-bold text-emerald-800">{autoRows.length}</div>
                    <div className="mt-0.5 text-[11px] text-emerald-700/70">Rolling window · last 7</div>
                </div>
                <div className="rounded-xl border border-violet-100 bg-violet-50/30 p-4 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-violet-700">Manual snapshots</div>
                    <div className="mt-1 font-display text-2xl font-bold text-violet-800">{manualRows.length}</div>
                    <div className="mt-0.5 text-[11px] text-violet-700/70">Kept indefinitely</div>
                </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Available backups
                </div>
                {loading && rows.length === 0 && <div className="px-6 py-14 text-center text-sm text-slate-400">Loading…</div>}
                {!loading && rows.length === 0 && (
                    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center text-slate-400">
                        <Database size={32} /><div className="text-sm">No backups yet. Click "Create backup now" to snapshot the database.</div>
                    </div>
                )}
                <ul>
                    {rows.map((r) => (
                        <li key={r.name} data-testid={`backup-row-${r.name}`} className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-3 first:border-0 hover:bg-slate-50">
                            <div>
                                <div className="font-mono text-xs font-medium text-slate-900">{r.name}</div>
                                <div className="mt-0.5 text-[11px] text-slate-500">
                                    {formatWhen(r.created_at)} · {fmtBytes(r.size_bytes)}
                                    {r.auto
                                        ? <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">AUTO</span>
                                        : <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700">MANUAL</span>
                                    }
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => openRestore(r)} data-testid={`backup-restore-${r.name}`}
                                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                                    <ArrowCounterClockwise size={14} className="mr-1.5" /> Restore party
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => download(r.name)} data-testid={`backup-download-${r.name}`}>
                                    <DownloadSimple size={14} className="mr-1.5" /> Download
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
                <DialogContent className="max-w-2xl" data-testid="restore-dialog">
                    <DialogHeader>
                        <DialogTitle>Restore a party from backup</DialogTitle>
                        <DialogDescription>
                            {restoreBackup ? (
                                <>
                                    Searching inside <span className="font-mono text-xs">{restoreBackup.name}</span> ({formatWhen(restoreBackup.created_at)}). Type part of the party name / phone / GST and hit <b>Search</b>. Existing live data is never overwritten.
                                </>
                            ) : "Choose a backup first."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex gap-2">
                        <Input
                            data-testid="restore-search-input"
                            autoFocus
                            placeholder="e.g. chirag, 9876543210, 27AAAA…"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
                            className="flex-1"
                        />
                        <Button onClick={doSearch} disabled={searching} data-testid="restore-search-btn">
                            <MagnifyingGlass size={14} className="mr-1.5" />
                            {searching ? "Searching…" : "Search"}
                        </Button>
                    </div>
                    <div className="max-h-[55vh] overflow-y-auto rounded-md border border-slate-200">
                        {searchResults.length === 0 ? (
                            <div className="px-4 py-10 text-center text-xs text-slate-400">
                                {searching ? "Reading backup…" : "No results yet — enter a search term above."}
                            </div>
                        ) : (
                            <ul>
                                {searchResults.map((p) => (
                                    <li key={p.id} data-testid={`restore-hit-${p.id}`} className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 last:border-0 hover:bg-slate-50">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <div className="truncate text-sm font-semibold text-slate-900">{p.name}</div>
                                                {p.already_exists && (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                                                        <CheckCircle size={10} weight="fill" /> Live
                                                    </span>
                                                )}
                                                {!p.already_exists && (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
                                                        Deleted
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-0.5 text-[11px] text-slate-500 truncate">
                                                {p.phone ? `☎ ${p.phone} · ` : ""}
                                                {p.contact_person || "—"}
                                            </div>
                                            <div className="mt-0.5 text-[10px] font-mono text-slate-400">
                                                {p.backup_counts.transactions} txns · {p.backup_counts.uploads} slips · {p.backup_counts.users} portal login(s)
                                            </div>
                                        </div>
                                        <Button
                                            size="sm"
                                            data-testid={`restore-party-${p.id}`}
                                            disabled={restoringId === p.id}
                                            onClick={() => restoreParty(p)}
                                            className={p.already_exists
                                                ? "bg-slate-200 text-slate-700 hover:bg-slate-300"
                                                : "bg-emerald-600 text-white hover:bg-emerald-700"}
                                        >
                                            {restoringId === p.id ? "Restoring…" : (p.already_exists ? "Merge" : "Restore")}
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRestoreOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
